<template>
  <section>
    <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
      <div class="tw-col-span-1 tw-mb-2">
        <cl-form-group>
          <cl-form-label
            label-for="name"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("name") }}
          </cl-form-label>
          <cl-form-input
            :id="'name'"
            v-model:value="v$.name.$model"
            :name="'name'"
            :type="'text'"
            :placeholder="t('customer-name-placeholder')"
            :autofocus="isCurrentStep"
            :state="!v$.name.$dirty ? null : !v$.name.$error"
            :data-test-id="'update-customer-input-name'"
            @blur="handleNameBlur"
            @input="v$.name.$reset()"
          />
          <span v-if="!v$.name.$error" class="tw-text-sm">
            {{ t("customer-name-hint") }}
          </span>
          <span
            v-if="v$.name.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.name.required.$response">
              {{ t("required-field") }}
            </span>
            <span
              v-else-if="
                !v$.name.maxLength.$response || !v$.name.minLength.$response
              "
            >
              {{ t("customer-name-validation") }}
            </span>
          </span>
        </cl-form-group>
      </div>
    </div>
    <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
      <div class="tw-col-span-1 tw-mb-2">
        <cl-form-group>
          <cl-form-label
            label-for="description"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("description-label") }}
          </cl-form-label>
          <cl-form-input
            :id="'description'"
            v-model:value="v$.description.$model"
            :name="'description'"
            :type="'text'"
            :placeholder="t('description-placeholder')"
            :data-test-id="'update-customer-input-description'"
            :state="!v$.description.$dirty ? undefined : !v$.description.$error"
          />
          <span
            v-if="v$.description.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.description.maxLength.$response">
              {{ t("description-255-characters") }}
            </span>
          </span>
        </cl-form-group>
      </div>
    </div>
  </section>
</template>
<script lang="ts">
type Form = {
  name: string;
  description: string;
  uuid: string;
};

interface Props {
  form: Form;
  isCurrentStep: boolean;
}
</script>
<script setup lang="ts">
import { reactive, watch, watchEffect } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import { useI18n } from "vue-i18n";

const props = defineProps<Props>();
const form = reactive<Form>({ ...props.form });
const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:form", form: Form): void;
  (e: "formValidation", isValid: boolean): void;
}>();

const rules = {
  name: { required, minLength: minLength(3), maxLength: maxLength(100) },
  description: { maxLength: maxLength(255) },
};

const v$ = useVuelidate(rules, form);
const handleNameBlur = async () => {
  const isValid = await v$.value.name.$validate();
  if (!isValid) {
    return;
  }
};

watch(form, (newValue) => {
  emit("update:form", newValue);
});

watchEffect(() => {
  emit("formValidation", v$.value.$invalid);
});
</script>
